/**
 * AuthContext (P4 rewrite, 2026-05-23)
 *
 * Cookie-based auth state. No token in localStorage — the browser holds
 * the __Host-ddarp_session cookie and we infer "logged in" from a
 * successful /api/auth-new/me call on mount + after login.
 *
 * Public API:
 *   const { user, isLoading, isAuthenticated, login, logout, hasRole }
 *     = useAuth();
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { authApi, AUTH_UNAUTHORIZED_EVENT, type MeResponse } from '@/api/client';

export type Role = 'admin' | 'partner' | 'user';

export interface User {
  id: number;
  email: string;
  role: Role;
  username?: string | null;
  company?: string | null;
  lastLoginAt?: string | null;
  lastLoginIp?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  hasRole: (role: Role) => boolean;
  refresh: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

function meToUser(me: MeResponse): User {
  return {
    id: me.id,
    email: me.email,
    role: me.role,
    username: me.username ?? null,
    company: me.company ?? null,
    lastLoginAt: me.last_login_at ?? null,
    lastLoginIp: me.last_login_ip ?? null,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(meToUser(me));
    } catch {
      setUser(null);
    }
  }, []);

  // On mount: try to restore the session via /me. 401 just means "not logged in".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(meToUser(me));
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // React to the soft-401 event from api/client.ts — clear local state so
  // the next render decides whether to redirect via ProtectedRoute.
  useEffect(() => {
    function onUnauthorized() {
      setUser(null);
    }
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe = false): Promise<User> => {
    setError(null);
    setIsLoading(true);
    try {
      const me = await authApi.login(email, password, rememberMe);
      const u = meToUser(me);
      setUser(u);
      return u;
    } catch (err: unknown) {
      // Backend returns generic "invalid email or password" on 401.
      // Don't reveal axios internals; surface the backend's detail if present.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const detail = (err as any)?.response?.data?.detail;
      const friendly =
        typeof detail === 'string' ? detail : 'invalid email or password';
      setError(friendly);
      throw new Error(friendly);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // ignore — server-side may already consider us logged out
    } finally {
      setUser(null);
    }
  }, []);

  const hasRole = useCallback(
    (role: Role): boolean => {
      if (!user) return false;
      // Hierarchy: admin → satisfies anything; partner → satisfies partner
      // or user; user → satisfies user only. So `hasRole('partner')`
      // returns true for both admins and partners.
      if (user.role === 'admin') return true;
      if (user.role === 'partner') return role === 'partner' || role === 'user';
      return user.role === role;
    },
    [user]
  );

  const clearError = useCallback(() => setError(null), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        error,
        login,
        logout,
        hasRole,
        refresh,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
